import { PLANS_FEATURES_DISPLAY_NAME } from '@constants/pricing';
import { Cookies } from '@utils/cookie.utils';
import {
  AiPlan,
  PlanResponse,
  PricingPlans,
  PricingV2APIResponse,
  PublicUIPlan,
  PublicUILadder,
  PlanKey,
  AddonSubscriptionPlan,
  AddonSubscriptionPlanKey,
  PricingDuration,
  UIFeature,
  UsageFeature,
} from 'src/types/pricing.types';
import { sendRudderStackAnalyticEvent } from './analytics';
import { ChangeAddOnSeatsAnalytics } from 'src/types/analytics.types';

export function serialisePlan(
  resPlan: PlanResponse[],
  currentPlans: PricingPlans,
  options: { isLoggedIn?: boolean; theme?: 'light' | 'dark' } = {},
) {
  const { isLoggedIn = false, theme } = options;

  try {
    const currentPlanClone: PricingPlans = JSON.parse(
      JSON.stringify(currentPlans),
    );
    const assetsHost = `${process.env.NEXT_PUBLIC_CDN_URL}/homepage`;
    const baseHost =
      `${process.env.NEXT_PUBLIC_STUDIO_HOST}` || 'https://studio.invideo.io';

    if (currentPlanClone && Array.isArray(currentPlanClone)) {
      const newPlanData = currentPlanClone.map(currentPlan => {
        const currentResPlan = resPlan.find(
          plan => plan.name === currentPlan.name,
        );

        if (!currentResPlan) {
          return currentPlan;
        }

        currentPlan = { ...currentPlan, ...currentResPlan };

        if (currentPlan.CTA?.monthly?.url) {
          currentPlan.CTA.monthly.url = baseHost + currentPlan.CTA.monthly.url;
        }
        if (currentPlan.CTA?.annually?.url) {
          currentPlan.CTA.annually.url =
            baseHost + currentPlan.CTA.annually.url;
        }
        if (currentPlan.redirection) {
          currentPlan.redirection = {
            url: baseHost + currentPlan.redirection,
            type: '',
          };
        }

        if (currentPlan.featuresCheckImage === '') {
          if (theme === 'dark') {
            currentPlan.featuresCheckImage = `${assetsHost}/check-icon.svg`;
          } else {
            currentPlan.featuresCheckImage =
              'https://web-assets.invideo.io/common/prod/icons/green-tick.png';
          }
        }

        if (
          currentPlan.appPromo &&
          !currentPlan.appPromo?.appImage &&
          currentPlan.label === 'Business'
        ) {
          currentPlan.appPromo.appImage = assetsHost + '/filmr-mobile.png';
        }

        if (
          currentPlan.appPromo &&
          !currentPlan.appPromo?.appImage &&
          currentPlan.label === 'Unlimited'
        ) {
          currentPlan.appPromo.appImage =
            assetsHost + '/filmr-mobile-unlimited.png';
        }

        if (
          currentPlan.planPrice &&
          currentPlan.planPrice &&
          currentPlan.label !== 'Free' &&
          currentResPlan
        ) {
          currentPlan.planPrice.monthly.price =
            currentResPlan.durations[0].monthly_price_display;

          currentPlan.planPrice.annually.price = Math.round(
            currentResPlan.durations[1].monthly_price_display,
          );

          currentPlan.planPrice.annually.starText =
            currentPlan.planPrice.annually.starText?.replace(
              /\$\d+/,
              '$' + currentResPlan.durations[1].sellingPrice,
            );
        }

        currentPlan.CTA.monthly.text =
          isLoggedIn &&
          currentResPlan.durations[0].planAction?.action === 'NONE'
            ? 'Current Plan'
            : currentResPlan.durations[0].planAction?.displayText || '';

        currentPlan.CTA.monthly.url =
          currentResPlan.durations[0].planAction?.actionUrl || '';
        currentPlan.CTA.monthly.action =
          currentResPlan.durations[0].planAction?.action;

        currentPlan.CTA.annually.text =
          isLoggedIn &&
          currentResPlan.durations[1].planAction?.action === 'NONE'
            ? 'Current Plan'
            : currentResPlan.durations[1].planAction?.displayText || '';

        currentPlan.CTA.annually.url =
          currentResPlan.durations[1].planAction?.actionUrl || '';
        currentPlan.CTA.annually.action =
          currentResPlan.durations[1].planAction?.action;

        if (currentResPlan.durations) {
          currentPlan.durations = currentResPlan.durations;
        }

        return currentPlan;
      });

      return newPlanData;
    }
    return currentPlanClone;
  } catch (error) {
    console.log(error);
    return currentPlans;
  }
}

export function setPlanForCheckout({
  plan,
  duration,
  couponCode,
  domain,
}: {
  plan: string;
  duration: boolean;
  couponCode?: string;
  domain?: string;
}) {
  const data = {
    plan: plan,
    duration: duration,
    coupon: couponCode,
  };
  const cookieName = window.btoa('pricing_plan');
  const cookieValue = JSON.stringify(data);

  Cookies.set(cookieName, cookieValue, {
    path: '/',
    domain,
  });
}

export function setCookieForCoupon(couponCode: string, response: any) {
  const couponResponse = { ...response };

  if (couponResponse.pricing_plans) {
    delete couponResponse.pricing_plans;
  }

  const data = {
    coupon: couponCode,
    response: couponResponse,
  };

  const domain = window.location.hostname;

  const cookieName = window.btoa('coupon');
  const cookieValue = JSON.stringify(data);

  Cookies.set(cookieName, cookieValue, { sameSite: 'Lax', domain });
}

export const replaceTripleBraces = (template: string, json: any) => {
  if (template) {
    return template.replace(/{{{(.+?)}}}/g, (_, g1) => json[g1] ?? g1);
  }
  return '';
};

const getTickByPlan = (plan: string) => {
  const assetUrl = process.env.NEXT_PUBLIC_SHARED_CDN_URL || '';
  switch (plan) {
    case 'plus':
      return `${assetUrl}/pricing/launch-tick.svg`;
    case 'max':
      return `${assetUrl}/pricing/grow-tick.svg`;
    case 'generative':
      return `${assetUrl}/pricing/grow-tick.svg`;
    default:
      return `${assetUrl}/pricing/tick.svg`;
  }
};

export const serializeAiPlans = (
  plans: PricingV2APIResponse['plans'],
  current_plans: AiPlan[],
) => {
  const assetsUrl = process.env.NEXT_PUBLIC_SHARED_CDN_URL;
  return current_plans.map(plan => {
    const monthlyPricing = plans[plan.key]['monthly'];
    const yearlyPricing = plans[plan.key]['yearly'];

    const planPrice = {
      monthly: {
        currency: monthlyPricing.symbol || '$',
        price: monthlyPricing.amount,
        planId: monthlyPricing.plan_id,
      },
      yearly: {
        currency: yearlyPricing.symbol || '$',
        price: yearlyPricing.amount / 12,
        planId: yearlyPricing.plan_id,
      },
    };

    const usageFeatures = plans[plan.key].usage_features ?? [];

    const usageQuota = plans[plan.key].usage_quota;

    const key_features = usageFeatures
      .filter(f => f.top_feature)
      .filter(f => f.tags.includes('public'));

    return {
      ...plan,
      planPrice,
      key_features,
      usageQuota,
      sequence: plans[plan.key].sequence,
      supported_item_plans: plans[plan.key].supported_item_plans,
    };
  });
};

export const serializeAiPlansFeatures = (
  plans: any,
  current_plans: AiPlan[],
  features: any,
) => {
  const assetsUrl = process.env.NEXT_PUBLIC_SHARED_CDN_URL;
  current_plans.forEach(plan => {
    const usageFeatures = plans[plan.key].usage_features ?? [];

    usageFeatures.forEach((f: any) => {
      if (features[f.feature]) {
        features[f.feature] = {
          ...features[f.feature],
          help: replaceTripleBraces(f.tooltip_description, {
            quota_unit: f.quota_unit,
            recurrence: f.recurrence,
            quota: f.quota,
          }),
          [plan.key]: {
            icon: getTickByPlan(plan.key),
            text: replaceTripleBraces(f.detailed_description, {
              quota_unit: f.quota_unit,
              recurrence: f.recurrence,
              quota: '{{{quota}}}',
            }),
            name: f.feature,
            quota: f.quota,
            style: '',
          },
        };
      } else {
        features[f.feature] = {
          name: f.feature,
          displayName: PLANS_FEATURES_DISPLAY_NAME[f.feature],
          help: replaceTripleBraces(f.tooltip_description, {
            quota_unit: f.quota_unit,
            recurrence: f.recurrence,
            quota: f.quota,
          }),
          [plan.key]: {
            icon:
              f.detailed_description?.toLowerCase() === 'not included'
                ? `${assetsUrl}/pricing/cross.svg`
                : getTickByPlan(plan.key),
            text: replaceTripleBraces(f.detailed_description, {
              quota_unit: f.quota_unit,
              recurrence: f.recurrence,
              quota: '{{{quota}}}',
            }),
            name: f.feature,
            quota: f.quota,
            style:
              f.detailed_description?.toLowerCase() === 'not included'
                ? 'iv-opacity-30'
                : '',
          },
        };
      }
    });
  });
  return features;
};

// New utilities for v40July ladder-based pricing (public pages)

/**
 * Builds ladder arrays for public pricing display (no subscription logic)
 */
export function buildPublicLadders(
  plan: PublicUIPlan,
  allAddonPlans: AddonSubscriptionPlan[],
): { cursor: number; ladders: PublicUILadder[] } {
  const supportedAddonPlans = allAddonPlans.filter(addonPlan =>
    plan.supported_item_plans.includes(addonPlan.id),
  );

  let cursor = 0;
  let ladders: PublicUILadder[] = [];

  if (plan.id === 'plus') {
    const basePlusLadder: PublicUILadder[] = [];

    // Apply across boost levels
    for (let boost = 1; boost <= 5; boost++) {
      const selectedAddonPlan =
        supportedAddonPlans.find(
          addonPlan => addonPlan.id === 'stock_v2_pack',
        ) || supportedAddonPlans[0];

      ladders.push(
        createBaseLadder({
          index: ladders.length + boost,
          addon: selectedAddonPlan,
          boostMultiplier: boost,
          addOnMultiplier: boost,
          prevBoost: boost > 1,
          nextBoost: boost < 5,
          nextCredit: boost > 1,
          prevCredit: boost < 5,
          planId: plan.id,
        }),
      );
    }
  }

  if (plan.id === 'max') {
    // Max Plan: 13 generative tiers across 5 boost levels
    const baseMaxLadder: PublicUILadder[] = [];

    // Apply across boost levels
    for (let boost = 1; boost <= 5; boost++) {
      ladders.push(
        createBaseLadder({
          index: ladders.length + boost,
          addon: null,
          boostMultiplier: boost,
          addOnMultiplier: boost,
          prevBoost: boost > 1,
          nextBoost: boost < 5,
          nextCredit: boost > 1,
          prevCredit: boost < 5,
          planId: plan.id,
        }),
      );
    }
  }

  if (plan.id === 'generative') {
    for (let i = 0; i < 10; i++) {
      const boostMultipliers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

      ladders.push(
        createBaseLadder({
          index: i,
          addon: null,
          addOnMultiplier: 0,
          boostMultiplier: boostMultipliers[i],
          planId: plan.id,
          prevBoost: i > 0,
          nextBoost: i < 14,
          prevCredit: i > 0,
          nextCredit: i < 14,
        }),
      );
    }
  }

  if (plan.id === 'team') {
    for (let i = 0; i < 15; i++) {
      const multipliers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 30, 40, 50];
      ladders.push(
        createBaseLadder({
          index: i,
          addon: null,
          addOnMultiplier: 0,
          boostMultiplier: multipliers[i],
          planId: plan.id,
          prevBoost: true,
          nextBoost: true,
          prevCredit: i > 0,
          nextCredit: i < 14,
        }),
      );
    }
  }

  if (plan.id === 'free') {
    // Free Plan: Single tier
    ladders.push(
      createBaseLadder({
        index: 0,
        addon: null,
        addOnMultiplier: 0,
        boostMultiplier: 1,
        planId: plan.id,
      }),
    );
  }

  const updatedLadders = ladders.map((ladder, index) => {
    return {
      ...ladder,
      index,
    };
  });

  return { cursor, ladders: updatedLadders };
}

/**
 * Creates a base ladder configuration
 */
function createBaseLadder(config: {
  index: number;
  addon: AddonSubscriptionPlan | null;
  addOnMultiplier: number;
  boostMultiplier: number;
  planId: PlanKey;
  prevBoost?: boolean;
  nextBoost?: boolean;
  prevCredit?: boolean;
  nextCredit?: boolean;
  boostHasAddons?: number;
}): PublicUILadder {
  return {
    index: config.index,
    addon: config.addon,
    boostHasAddons: config.boostHasAddons ?? 0,
    addOnMultiplier: config.addOnMultiplier,
    boostMultiplier: config.boostMultiplier,
    prevBoost: config.prevBoost ?? false,
    nextBoost: config.nextBoost ?? false,
    prevCredit: config.prevCredit ?? false,
    nextCredit: config.nextCredit ?? false,
    totalMonthlyPrice: 0,
    totalYearlyPrice: 0,
    yearlyCostPerCredit: 0,
    monthlyCostPerCredit: 0,
    credits: 0,
    uiFeatures: [],
    addonDescription: '',
    addOnFeatures: [],
    boostFeatures: [],
    asteriskText: {
      monthly: null,
      yearly: null,
    },
    ctaText: 'Get Started',
  };
}

/**
 * Builds addon description based on boost and credit multipliers
 */
function buildAddonDescription(
  plan: PublicUIPlan,
  ladder: PublicUILadder,
): string {
  const boostQuantity = ladder.boostMultiplier;
  const creditV2 = ladder.credits;
  const descriptions = [];

  if (boostQuantity > 0) {
    if (plan.id === 'plus' || plan.id === 'generative' || plan.id === 'max') {
      descriptions.push(`${boostQuantity}x Boost`);
    } else if (plan.id === 'team') {
      descriptions.push(`${boostQuantity} Seat${boostQuantity > 1 ? 's' : ''}`);
    }
  }

  if (creditV2 > 0) {
    descriptions.push(`${creditV2} Credit${creditV2 > 1 ? 's' : ''}`);
  }

  return descriptions.join(', ');
}

/**
 * Builds addon features similar to PricingStore.ts
 */
function buildAddOnFeatures(
  plan: PublicUIPlan,
  ladder: PublicUILadder,
): string[] {
  const creditV2 = ladder.credits;
  if (plan.id === 'plus' || plan.id === 'max' || plan.id === 'generative') {
    return [`${creditV2} credits`];
  }
  return [];
}

/**
 * Builds boost features similar to PricingStore.ts
 */
function buildBoostFeatures(
  plan: PublicUIPlan,
  ladder: PublicUILadder,
): string[] {
  const boostQuantity = ladder.boostMultiplier;
  const quotas: string[] = [];
  const usageQuota = plan.usageQuota;

  if (plan.id === 'plus' || plan.id === 'max' || plan.id === 'generative') {
    quotas.push(
      `${(usageQuota.premium_minutes ?? 0) * boostQuantity} Video mins`,
    );
    quotas.push(`${(usageQuota.premium_media ?? 0) * boostQuantity} iStocks`);
  }

  if (plan.id === 'team') {
    quotas.push(`${(usageQuota.credits_v2 ?? 0) * boostQuantity} credits`);
    quotas.push(
      `${(usageQuota.premium_minutes ?? 0) * boostQuantity} Video mins`,
    );
    quotas.push(
      `${((usageQuota as any).user_count ?? 0) * boostQuantity} seats`,
    );
  }

  return quotas;
}

/**
 * Helper function to get monthly equivalent amount (matches PricingStore.ts pattern)
 */
function amountPerMonth(
  plan: { monthly: { amount: number }; yearly: { amount: number } },
  planType: 'monthly' | 'yearly',
): number {
  if (planType === 'monthly') {
    return plan.monthly.amount;
  }
  if (planType === 'yearly') {
    return plan.yearly.amount / 12;
  }
  return 0;
}

/**
 * Calculate final price using PricingStore.ts logic
 */
function finalPrice({
  plan,
  addonPlan,
  planType,
  planBoost,
  generativeAddonBoost,
}: {
  plan: PublicUIPlan;
  addonPlan: AddonSubscriptionPlan | null;
  planType: 'monthly' | 'yearly';
  planBoost: number;
  generativeAddonBoost: number;
}): number {
  if (!plan || plan.isFreePlan) {
    return 0;
  }

  const packAmount = addonPlan
    ? generativeAddonBoost * amountPerMonth(addonPlan, planType)
    : 0;

  const price = amountPerMonth(plan, planType) * planBoost + packAmount;

  return Math.round(price);
}

/**
 * Extract placeholders from a string (similar to PricingStore.ts)
 */
function extractPlaceholders(str: string): string[] {
  const regex = /{{{([^}]+)}}}/g;
  const matches = str.match(regex);

  if (!matches) return [];

  return matches.map(match => match.slice(3, -3));
}

/**
 * Get pricing value from credits (similar to PricingStore.ts)
 */
function getPricingValueFromCredits(credits: number, pricing: string): number {
  switch (pricing) {
    case 'ai_credits':
      return Math.floor(credits / 0.333);
    case 'ugc_ads':
      return Math.floor(credits / 5) < 1 ? 0 : Math.floor(credits / 5);
    case 'generative_video':
      return Math.floor(credits / 0.333) < 1 ? 1 : Math.floor(credits / 0.333);
    case 'stock_ads':
      return Math.floor(credits / 5) < 1 ? 1 : Math.floor(credits / 5);
    case 'stock_video':
      return Math.floor(credits / 0.5) < 1 ? 1 : Math.floor(credits / 0.5);
    case 'generative_image':
      return Math.floor(credits / 0.22) < 1 ? 1 : Math.floor(credits / 0.2);
    case 'generative_clip':
      return Math.floor(credits / 0.333) < 1 ? 1 : Math.floor(credits / 0.333);
    default:
      return 0;
  }
}

/**
 * Builds UI features similar to PricingStore.ts
 */
function buildUIFeatures(
  plan: PublicUIPlan,
  ladder: PublicUILadder,
): UIFeature[] {
  const creditV2 = ladder.credits;
  const boostQuantity = ladder.boostMultiplier;
  const creditQuantity =
    ladder.addOnMultiplier +
    (ladder.boostHasAddons ?? 0) * ladder.boostMultiplier;

  const features = plan.features.map(feature => {
    if (feature.id === 'generative_features' && 'type' in feature) {
      const placeholders = extractPlaceholders(feature.description);
      const finalMessage = placeholders.reduce(
        (acc: string, placeholder: string) => {
          const value = getPricingValueFromCredits(creditV2, placeholder);

          switch (placeholder) {
            case 'generative_clip': {
              let finalValue: string | number = value;
              if (value > 600) {
                finalValue = Math.floor(value / 60).toString() + ' mins';
              } else {
                finalValue = finalValue.toString() + ' secs';
              }
              return acc.replace(`{{{${placeholder}}}}`, finalValue);
            }
            case 'generative_video': {
              let finalValue: string | number = value;
              if (value > 600) {
                finalValue = Math.floor(value / 60).toString() + ' mins';
              } else {
                finalValue = finalValue.toString() + ' secs';
              }
              return acc.replace(`{{{${placeholder}}}}`, finalValue);
            }
            case 'ai_credits': {
              let finalValue: string | number = value;
              const seconds = value;
              if (seconds === 0) {
                finalValue = 'No generative videos';
              } else if (seconds > 600) {
                finalValue =
                  Math.floor(seconds / 60).toString() +
                  ' mins of generative videos';
              } else {
                finalValue = seconds.toString() + ' secs of generative video';
              }
              return acc.replace(`{{{${placeholder}}}}`, finalValue);
            }
            case 'ugc_ads': {
              let finalValue: string | number = value;
              if (value === 0) {
                finalValue = 'No UGC ads';
              } else if (value === 1) {
                finalValue = `${value} UGC product asset ad`;
              } else {
                finalValue = `${value} UGC product asset ads`;
              }

              if (plan.id === 'generative' || plan.id === 'team') {
                const value = getPricingValueFromCredits(
                  creditV2 / 4,
                  placeholder,
                );
                if (value === 0) {
                  finalValue = 'No UGC ads';
                } else if (value === 1) {
                  finalValue = `${value} generative UGC ad`;
                } else {
                  finalValue = `${value} generative UGC ads`;
                }
              }
              return acc.replace(`{{{${placeholder}}}}`, finalValue);
            }
            default:
              return acc.replace(`{{{${placeholder}}}}`, value.toString());
          }
        },
        feature.description,
      );
      return {
        type: feature.type,
        actualType: feature.type,
        description: finalMessage,
        tooltip: feature.tooltip,
      };
    }

    if (feature.id === 'additional_features' && 'type' in feature) {
      const placeholders = extractPlaceholders(feature.description);

      const finalMessage = placeholders.reduce(
        (acc: string, placeholder: string) => {
          let finalValue: string | number = '';
          const value = plan.usageQuota[
            placeholder as keyof typeof plan.usageQuota
          ] as number;
          let valuesForSeats = value * boostQuantity;
          if (plan.id !== 'team' && placeholder === 'user_count') {
            valuesForSeats = 3;
          }

          if (placeholder === 'storage') {
            // Simple storage conversion (could be enhanced)
            const storageGB = valuesForSeats;
            if (storageGB >= 1024) {
              finalValue = `${Math.round(storageGB / 1024)}TB`;
            } else {
              finalValue = `${storageGB}GB`;
            }
            return acc.replace(`{{{${placeholder}}}}GB`, finalValue.toString());
          }

          if (plan.id === 'team') {
            finalValue = `${valuesForSeats} ${
              valuesForSeats === 1 ? 'seat' : 'seats'
            }`;
          } else {
            finalValue = `${valuesForSeats} ${
              valuesForSeats === 1 ? 'user' : 'users'
            }`;
          }

          return acc.replace(`{{{${placeholder}}}}`, finalValue.toString());
        },
        feature.description,
      );

      return {
        type: feature.type,
        actualType: feature.type,
        description: finalMessage,
        tooltip: feature.tooltip,
      };
    }

    let label = '';
    if ('description' in feature) {
      label = feature.description;
    }

    if (feature.id === 'additional_features' && 'type' in feature) {
      label = label.replace('{{{user_count}}}', boostQuantity.toString());

      if (boostQuantity > 1) {
        label = label.replace('user', 'users');
      }
    }

    const placeholders = extractPlaceholders(label);

    const usageQuota = plan.usageQuota;
    const addonUsageQuota = ladder.addon?.usage_quota ?? {};

    placeholders.forEach(placeholder => {
      const finalQuota =
        ((usageQuota[placeholder as keyof typeof usageQuota] as number) ?? 0) *
          boostQuantity +
        ((addonUsageQuota[
          placeholder as keyof typeof addonUsageQuota
        ] as number) ?? 0) *
          creditQuantity;
      if (placeholder === 'credits_v2') {
        label = label.replace('{{{credits_v2}}}', creditV2.toString());
      } else if (placeholder === 'storage') {
        // Simple storage conversion
        const storageGB = finalQuota;
        if (storageGB >= 1024) {
          label = label.replace(
            '{{{storage}}}GB',
            `${Math.round(storageGB / 1024)}TB`,
          );
        } else {
          label = label.replace('{{{storage}}}GB', `${storageGB}GB`);
        }
      } else {
        label = label.replace(`{{{${placeholder}}}}`, finalQuota.toString());
      }
    });

    return {
      type: feature.id,
      actualType: feature.type,
      description: label,
      tooltip: feature.tooltip,
    };
  });

  return features.map((feature, index) => {
    return {
      ...feature,
      id: `${feature.type}-${index}`,
    };
  });
}

/**
 * Updates ladder pricing calculations
 */
export function updateLadderPrice(
  plan: PublicUIPlan,
  ladder: PublicUILadder,
  selectedDuration: PricingDuration,
): PublicUILadder {
  const totalAddonMultiplier =
    ladder.addOnMultiplier +
    (ladder.boostHasAddons ?? 0) * ladder.boostMultiplier;

  // Calculate prices using the same logic as PricingStore.ts
  const totalMonthlyPrice = finalPrice({
    plan: plan,
    addonPlan: ladder.addon,
    planType: 'monthly',
    planBoost: ladder.boostMultiplier,
    generativeAddonBoost: totalAddonMultiplier,
  });

  const totalYearlyPrice = finalPrice({
    plan: plan,
    addonPlan: ladder.addon,
    planType: 'yearly',
    planBoost: ladder.boostMultiplier,
    generativeAddonBoost: totalAddonMultiplier,
  });

  const baseCredits = plan.usageQuota.credits_v2 ?? 0;
  const addonCredits = ladder.addon?.usage_quota.credits_v2 ?? 0;

  const aiCredits = baseCredits * ladder.boostMultiplier;
  const totalAddonCredits = addonCredits * totalAddonMultiplier;
  const totalCredits = aiCredits + totalAddonCredits;

  const yearlyCostPerCredit = parseFloat(
    (Math.ceil((totalYearlyPrice * 10) / totalCredits) / 10).toFixed(1),
  );
  const monthlyCostPerCredit = parseFloat(
    (Math.ceil((totalMonthlyPrice * 10) / totalCredits) / 10).toFixed(1),
  );

  const updatedLadder = {
    ...ladder,
    totalMonthlyPrice,
    totalYearlyPrice,
    credits: totalCredits,
    yearlyCostPerCredit,
    monthlyCostPerCredit,
  };

  return {
    ...updatedLadder,
    addonDescription: buildAddonDescription(plan, updatedLadder),
    uiFeatures: buildUIFeatures(plan, updatedLadder),
    addOnFeatures: buildAddOnFeatures(plan, updatedLadder),
    boostFeatures: buildBoostFeatures(plan, updatedLadder),
    asteriskText: {
      monthly: null,
      yearly: null,
    },
  };
}

/**
 * Builds UI plans for public display
 */
export function buildPublicUIPlans(
  plans: PricingV2APIResponse['plans'],
  addonPlans: PricingV2APIResponse['addon_subscription_plans'],
  selectedDuration: PricingDuration = PricingDuration.YEARLY,
): PublicUIPlan[] {
  const allAddonPlans = Object.entries(addonPlans).map(([key, addonPlan]) => ({
    ...addonPlan,
    id: key as AddonSubscriptionPlanKey,
  }));

  return Object.entries(plans).map(([planKey, planData]) => {
    const basePlan: PublicUIPlan = {
      id: planKey as PlanKey,
      name: planData.monthly.plan_id, // Use plan_id as name, or could be from display_name if available
      deprecated: planData.deprecated,
      tags: [],
      description: '', // Would come from CMS or API
      mobileDescription: '',
      monthly: {
        planId: planData.monthly.plan_id,
        amount: planData.monthly.amount,
        symbol: planData.monthly.symbol,
      },
      yearly: {
        planId: planData.yearly.plan_id,
        amount: planData.yearly.amount,
        symbol: planData.yearly.symbol,
      },
      supported_item_plans: planData.supported_item_plans || [],
      additionalFeatures: planData.usage_features || [],
      usageQuota: planData.usage_quota,
      isFreePlan: planKey === 'free',
      subtitle: '',
      features: planData.usage_features || [],
      cursor: 0,
      ladders: [],
    };

    const { cursor, ladders } = buildPublicLadders(basePlan, allAddonPlans);

    // Update ladder pricing
    const pricedLadders = ladders.map(ladder =>
      updateLadderPrice(basePlan, ladder, selectedDuration),
    );

    return {
      ...basePlan,
      cursor,
      ladders: pricedLadders,
    };
  });
}

/**
 * Helper function to calculate ladder addon amount (similar to PricingStore)
 */
function ladderAddonAmount(
  ladder: PublicUILadder,
  selectedDuration: PricingDuration,
): number {
  const addonPlan = ladder.addon;
  const totalAddOnMultiplier =
    ladder.addOnMultiplier +
    (ladder.boostHasAddons ?? 0) * ladder.boostMultiplier;

  if (selectedDuration === PricingDuration.MONTHLY) {
    return (addonPlan?.monthly.amount ?? 0) * totalAddOnMultiplier;
  } else {
    return (addonPlan?.yearly.amount ?? 0) * totalAddOnMultiplier;
  }
}

/**
 * Updates plan cursor for navigation (matches PricingStore logic)
 */
export function updatePublicCursor(
  plans: PublicUIPlan[],
  planId: PlanKey,
  type: 'increment' | 'decrement',
  cursorType: 'credit' | 'boost',
  selectedDuration: PricingDuration = PricingDuration.YEARLY,
): PublicUIPlan[] {
  return plans.map(plan => {
    if (plan.id === planId) {
      let newCursor = plan.cursor;

      if (cursorType === 'credit') {
        // Simple sequential navigation for credits (matches updatePlanCreditCursor)
        newCursor = plan.cursor + (type === 'increment' ? 1 : -1);

        // Ensure cursor stays within bounds
        if (newCursor >= plan.ladders.length) {
          newCursor = plan.ladders.length - 1;
        }
        if (newCursor < 0) {
          newCursor = 0;
        }
      } else if (cursorType === 'boost') {
        // Complex boost navigation (matches updatePlanBoostCursor logic)
        const currentLadder = plan.ladders[plan.cursor];
        const currentBoostMultiplier = currentLadder.boostMultiplier;
        const currentAddonPrice = ladderAddonAmount(
          currentLadder,
          selectedDuration,
        );

        if (type === 'increment') {
          const nextPossibleCursors = plan.ladders.filter(
            ladder => ladder.boostMultiplier > currentBoostMultiplier,
          );
          const nextCursor = nextPossibleCursors.find(ladder => {
            const addonPrice = ladderAddonAmount(ladder, selectedDuration);
            return addonPrice >= currentAddonPrice;
          });
          if (nextCursor) {
            newCursor = nextCursor.index;
          } else if (nextPossibleCursors.length > 0) {
            newCursor = nextPossibleCursors[0].index;
          }
        } else {
          const prevPossibleCursors = plan.ladders.filter(
            ladder => ladder.boostMultiplier < currentBoostMultiplier,
          );
          const prevCursor = prevPossibleCursors.findLast(
            ladder =>
              ladderAddonAmount(ladder, selectedDuration) <= currentAddonPrice,
          );
          if (prevCursor) {
            newCursor = prevCursor.index;
          }
        }
      }

      const ladder = plan.ladders[newCursor];
      let addonPlanPriceId = '';
      let planPriceId = '';
      if (ladder.addon) {
        if (selectedDuration === PricingDuration.MONTHLY) {
          addonPlanPriceId = ladder.addon.monthly.plan_id;
        } else {
          addonPlanPriceId = ladder.addon.yearly.plan_id;
        }
      }
      if (selectedDuration === PricingDuration.MONTHLY) {
        planPriceId = plan.monthly.planId;
      } else {
        planPriceId = plan.yearly.planId;
      }
      const analyticsProperties: ChangeAddOnSeatsAnalytics['event_properties'] =
        {
          type: 'decrement',
          plan_price_id: planPriceId,
          seats: ladder.boostMultiplier,
          addon_plan_price_id: addonPlanPriceId,
          addon_plan_seats: ladder.addOnMultiplier,
        };

      sendRudderStackAnalyticEvent({
        event_name:
          cursorType === 'credit' ? 'change-addon-seats' : 'change-seats',
        event_properties: analyticsProperties,
      });

      return {
        ...plan,
        cursor: newCursor,
      };
    }
    return plan;
  });
}

/**
 * Calculate final price for a plan configuration
 */
export function calculatePublicPrice(
  plan: PublicUIPlan,
  ladderIndex: number,
  duration: PricingDuration,
): number {
  const ladder = plan.ladders[ladderIndex];
  if (!ladder) return 0;

  return duration === PricingDuration.MONTHLY
    ? ladder.totalMonthlyPrice
    : ladder.totalYearlyPrice;
}
